import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  X, 
  Coffee, 
  Sun, 
  Moon, 
  Cookie,
  Loader,
  MessageCircle
} from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import { MACRO_CONFIG, formatDateDisplay, getToday } from '../../../utils/foodLogApi';

/**
 * Meal type icons and config
 */
const MEAL_CONFIG = {
  breakfast: { icon: Coffee, label: 'Breakfast', color: '#F59E0B' },
  lunch: { icon: Sun, label: 'Lunch', color: '#10B981' },
  dinner: { icon: Moon, label: 'Dinner', color: '#6366F1' },
  snack: { icon: Cookie, label: 'Snacks', color: '#EC4899' }
};

/**
 * MacroBar - Single progress bar for a macro
 */
function MacroBar({ macro, current, target, onRemove }) {
  const config = MACRO_CONFIG[macro];
  if (!config) return null;

  const percentage = Math.min(100, Math.round((current / target) * 100));
  const isOver = current > target;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '6px 0'
    }}>
      <div style={{
        width: '70px',
        fontSize: '12px',
        fontWeight: '500',
        color: ABLE_COLORS.textSecondary
      }}>
        {config.label}
      </div>
      <div style={{
        flex: 1,
        height: '8px',
        background: ABLE_COLORS.bgSubtle,
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          background: isOver ? '#EF4444' : config.color,
          borderRadius: '4px',
          transition: 'width 0.3s ease'
        }} />
      </div>
      <div style={{
        width: '60px',
        fontSize: '12px',
        fontWeight: '600',
        color: isOver ? '#EF4444' : ABLE_COLORS.textPrimary,
        textAlign: 'right'
      }}>
        {Math.round(current)}/{target}{config.unit !== 'kcal' ? config.unit : ''}
      </div>
      {onRemove && (
        <button
          onClick={() => onRemove(macro)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px',
            color: ABLE_COLORS.textMuted,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * FoodEntry - Single food item in the log
 */
function FoodEntry({ entry, onDelete }) {
  const isPlanned = entry.isPlanned && !entry.isActual;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 12px',
      background: isPlanned ? `${ABLE_COLORS.primary}08` : 'transparent',
      borderRadius: '8px',
      opacity: isPlanned ? 0.7 : 1
    }}>
      <div style={{
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: isPlanned ? ABLE_COLORS.textMuted : ABLE_COLORS.primary,
        border: isPlanned ? `1px dashed ${ABLE_COLORS.textMuted}` : 'none'
      }} />
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '500',
          color: ABLE_COLORS.textPrimary
        }}>
          {entry.name}
        </div>
        {entry.macros && Object.keys(entry.macros).length > 0 && (
          <div style={{
            fontSize: '11px',
            color: ABLE_COLORS.textMuted,
            marginTop: '2px'
          }}>
            {entry.macros.protein && `${entry.macros.protein}g protein`}
            {entry.macros.calories && ` • ${entry.macros.calories} cal`}
          </div>
        )}
      </div>
      {isPlanned && (
        <span style={{
          fontSize: '10px',
          color: ABLE_COLORS.textMuted,
          fontStyle: 'italic'
        }}>
          planned
        </span>
      )}
      {onDelete && !isPlanned && (
        <button
          onClick={() => onDelete(entry.id)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: ABLE_COLORS.textMuted
          }}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * MealSlot - Section for a meal type (breakfast, lunch, etc.)
 */
function MealSlot({ mealType, entries, onAddClick, onDeleteEntry }) {
  const config = MEAL_CONFIG[mealType];
  const Icon = config.icon;

  return (
    <div style={{
      background: ABLE_COLORS.neutral,
      borderRadius: ABLE_COLORS.cardRadiusLarge,
      border: `1px solid ${ABLE_COLORS.border}`,
      overflow: 'hidden',
      marginBottom: '12px'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 16px',
        borderBottom: entries.length > 0 ? `1px solid ${ABLE_COLORS.borderLight}` : 'none'
      }}>
        <Icon size={18} color={config.color} />
        <span style={{
          fontSize: '14px',
          fontWeight: '600',
          color: ABLE_COLORS.textPrimary,
          flex: 1
        }}>
          {config.label}
        </span>
        <button
          onClick={() => onAddClick(mealType)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 10px',
            background: ABLE_COLORS.bgSubtle,
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            color: ABLE_COLORS.textSecondary
          }}
        >
          <Plus size={14} />
          Add
        </button>
      </div>

      {/* Entries */}
      {entries.length > 0 && (
        <div style={{ padding: '4px 8px 8px' }}>
          {entries.map(entry => (
            <FoodEntry 
              key={entry.id} 
              entry={entry} 
              onDelete={onDeleteEntry}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * QuickAddModal - Simple modal to add food entry
 */
function QuickAddModal({ mealType, onAdd, onClose }) {
  const [name, setName] = useState('');
  const [protein, setProtein] = useState('');
  const [calories, setCalories] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    const macros = {};
    if (protein) macros.protein = parseFloat(protein);
    if (calories) macros.calories = parseFloat(calories);

    onAdd({
      mealType,
      name: name.trim(),
      macros
    });
    onClose();
  };

  const config = MEAL_CONFIG[mealType];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        width: '100%',
        maxWidth: '400px',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary
          }}>
            Add to {config.label}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={20} color={ABLE_COLORS.textMuted} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What did you eat?"
            autoFocus
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: '8px',
              marginBottom: '12px',
              boxSizing: 'border-box'
            }}
          />

          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: ABLE_COLORS.textMuted,
                marginBottom: '4px'
              }}>
                Protein (g)
              </label>
              <input
                type="number"
                value={protein}
                onChange={(e) => setProtein(e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: ABLE_COLORS.textMuted,
                marginBottom: '4px'
              }}>
                Calories
              </label>
              <input
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
                placeholder="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  border: `1px solid ${ABLE_COLORS.border}`,
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={!name.trim()}
            style={{
              width: '100%',
              padding: '12px',
              background: name.trim() ? ABLE_COLORS.primary : ABLE_COLORS.bgSubtle,
              color: name.trim() ? 'white' : ABLE_COLORS.textMuted,
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: name.trim() ? 'pointer' : 'not-allowed'
            }}
          >
            Add Entry
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * AddMacroModal - Modal to add a new macro to track
 */
function AddMacroModal({ availableMacros, trackedMacros, onAdd, onClose }) {
  const untrackedMacros = availableMacros.filter(m => !trackedMacros.includes(m));

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        width: '100%',
        maxWidth: '300px',
        padding: '20px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary
          }}>
            Add Macro to Track
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={20} color={ABLE_COLORS.textMuted} />
          </button>
        </div>

        {untrackedMacros.length === 0 ? (
          <p style={{
            color: ABLE_COLORS.textMuted,
            fontSize: '14px',
            textAlign: 'center',
            margin: '20px 0'
          }}>
            You're already tracking all available macros!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {untrackedMacros.map(macro => {
              const config = MACRO_CONFIG[macro];
              return (
                <button
                  key={macro}
                  onClick={() => {
                    onAdd(macro);
                    onClose();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    background: ABLE_COLORS.bgSubtle,
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '3px',
                    background: config?.color || ABLE_COLORS.textMuted
                  }} />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: ABLE_COLORS.textPrimary
                  }}>
                    {config?.label || macro}
                  </span>
                  <span style={{
                    fontSize: '12px',
                    color: ABLE_COLORS.textMuted,
                    marginLeft: 'auto'
                  }}>
                    {config?.unit || ''}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * LogTab - Daily food log with macro tracking
 */
export default function LogTab({
  // Food log state from hook
  selectedDate,
  entries = [],
  entriesByMeal = {},
  totals = {},
  isLoading = false,
  isSaving = false,
  isToday = true,
  // Preferences
  trackedMacros = ['protein', 'carbs', 'fat'],
  dailyTargets = {},
  availableMacros = [],
  // Actions
  onAddEntry,
  onDeleteEntry,
  onDateChange,
  onGoToToday,
  onAddTrackedMacro,
  onRemoveTrackedMacro,
  // Toast
  showToast
}) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalMealType, setAddModalMealType] = useState('snack');
  const [macroModalOpen, setMacroModalOpen] = useState(false);

  // Navigate dates
  const goToPreviousDay = () => {
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    onDateChange?.(prev.toISOString().split('T')[0]);
  };

  const goToNextDay = () => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    onDateChange?.(next.toISOString().split('T')[0]);
  };

  // Handle add click from meal slot
  const handleAddClick = (mealType) => {
    setAddModalMealType(mealType);
    setAddModalOpen(true);
  };

  // Handle add entry
  const handleAddEntry = async (entry) => {
    const result = await onAddEntry?.(entry);
    if (result) {
      showToast?.(`Added ${entry.name}`);
    }
  };

  // Handle delete entry
  const handleDeleteEntry = async (id) => {
    const entry = entries.find(e => e.id === id);
    const result = await onDeleteEntry?.(id);
    if (result) {
      showToast?.(`Removed ${entry?.name || 'entry'}`);
    }
  };

  // Check if can go to next day (not future)
  const today = getToday();
  const canGoNext = selectedDate < today;

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* Date Navigation */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        border: `1px solid ${ABLE_COLORS.border}`,
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={goToPreviousDay}
            style={{
              background: ABLE_COLORS.bgSubtle,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: 'pointer',
              padding: '10px',
              color: ABLE_COLORS.textSecondary,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronLeft size={20} />
          </button>

          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: ABLE_COLORS.textPrimary
            }}>
              {isToday ? 'Today' : formatDateDisplay(selectedDate)}
            </div>
            {!isToday && (
              <button
                onClick={onGoToToday}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: ABLE_COLORS.primary,
                  padding: '4px'
                }}
              >
                Back to today
              </button>
            )}
          </div>

          <button
            onClick={goToNextDay}
            disabled={!canGoNext}
            style={{
              background: ABLE_COLORS.bgSubtle,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: canGoNext ? 'pointer' : 'not-allowed',
              padding: '10px',
              color: ABLE_COLORS.textSecondary,
              display: 'flex',
              alignItems: 'center',
              opacity: canGoNext ? 1 : 0.3
            }}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Macro Bars */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        border: `1px solid ${ABLE_COLORS.border}`,
        padding: '16px',
        marginBottom: '16px'
      }}>
        {trackedMacros.map(macro => (
          <MacroBar
            key={macro}
            macro={macro}
            current={totals[macro] || 0}
            target={dailyTargets[macro] || 100}
            onRemove={trackedMacros.length > 1 ? onRemoveTrackedMacro : null}
          />
        ))}
        
        {availableMacros.length > trackedMacros.length && (
          <button
            onClick={() => setMacroModalOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              width: '100%',
              padding: '10px',
              marginTop: '8px',
              background: ABLE_COLORS.bgSubtle,
              border: `1px dashed ${ABLE_COLORS.border}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              color: ABLE_COLORS.textMuted
            }}
          >
            <Plus size={14} />
            Add macro to track
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px'
        }}>
          <Loader 
            size={24} 
            color={ABLE_COLORS.primary}
            style={{ animation: 'spin 1s linear infinite' }}
          />
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Meal Slots */}
      {!isLoading && (
        <>
          <MealSlot
            mealType="breakfast"
            entries={entriesByMeal.breakfast || []}
            onAddClick={handleAddClick}
            onDeleteEntry={handleDeleteEntry}
          />
          <MealSlot
            mealType="lunch"
            entries={entriesByMeal.lunch || []}
            onAddClick={handleAddClick}
            onDeleteEntry={handleDeleteEntry}
          />
          <MealSlot
            mealType="dinner"
            entries={entriesByMeal.dinner || []}
            onAddClick={handleAddClick}
            onDeleteEntry={handleDeleteEntry}
          />
          <MealSlot
            mealType="snack"
            entries={entriesByMeal.snack || []}
            onAddClick={handleAddClick}
            onDeleteEntry={handleDeleteEntry}
          />
        </>
      )}

      {/* Mabel Reminder */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '16px',
        color: ABLE_COLORS.textMuted,
        fontSize: '13px'
      }}>
        <MessageCircle size={16} color={ABLE_COLORS.warm} />
        <span>You can also tell Mabel what you ate and she'll log it for you</span>
      </div>

      {/* Quick Add Modal */}
      {addModalOpen && (
        <QuickAddModal
          mealType={addModalMealType}
          onAdd={handleAddEntry}
          onClose={() => setAddModalOpen(false)}
        />
      )}

      {/* Add Macro Modal */}
      {macroModalOpen && (
        <AddMacroModal
          availableMacros={availableMacros}
          trackedMacros={trackedMacros}
          onAdd={onAddTrackedMacro}
          onClose={() => setMacroModalOpen(false)}
        />
      )}
    </div>
  );
}
